<!-- Responsibility: List and select saved world blueprints.
Scope: Owns world gallery fetching and selection triggers. -->
<worlds-view class="menu-section active menu-overlay">
  <h3 class="lobby-section-title">WORLD GALLERY</h3>
  <p class="worlds-flow-hint">Pick a saved world below or forge a brand new world from this flow.</p>
  
  <button class="action-btn primary worlds-forge-btn" onclick={ forgeFresh }>FORGE NEW WORLD <span>+</span></button>
  
  <div class="save-list">
    <p if={ state.loading && !state.worlds.length } class="lobby-hint">Scanning worlds...</p>
    <p if={ state.error } class="lobby-error">{ state.error }</p>
    <p if={ !state.loading && !state.error && !state.worlds.length } class="lobby-hint">No reusable worlds found.</p>

    <div if={ state.loading && !state.worlds.length } class="loading-stack" aria-busy="true">
      <button each={ _ in LOADING_ROWS } type="button" class="world-slot loading-skeleton" disabled aria-hidden="true">
        <span class="world-slot-topline">
          <span class="world-slot-head loading-grid">
            <span class="loading-line long"></span>
            <span class="loading-line short"></span>
          </span>
          <span class="loading-chip"></span>
        </span>
        <span class="world-slot-preview loading-grid">
          <span class="loading-line full"></span>
          <span class="loading-line mid"></span>
        </span>
        <span class="world-slot-meta">
          <span class="loading-chip"></span>
          <span class="loading-chip" style="width: 7rem;"></span>
        </span>
      </button>
    </div>
    
    <button
      each={ world in state.worlds }
      type="button"
      class="world-slot"
      data-save-id={ world.saveId }
      disabled={ !!state.selectingWorldId }
      onclick={ selectWorld }
    >
      <span class="world-slot-topline">
        <span class="world-slot-head">
          <b>{ worldSeedLabel(world.seed) }</b>
          <small>{ worldOriginLabel(world.origin) }</small>
        </span>
        <span class="world-slot-route">OPEN WORLD</span>
      </span>
      <span class="world-slot-preview">
        <span class="world-slot-room">{ world.roomName }</span>
        <span class="world-slot-timestamp">{ worldTimestamp(world.timestamp) }</span>
      </span>
      <span class="world-slot-meta">
        <span class="world-slot-signal save">{ world.saveName }</span>
        <span class="world-slot-signal vessel">Last vessel: { world.playerIdentity }</span>
      </span>
      <span if={ state.selectingWorldId === world.saveId } class="world-slot-pending">LOADING...</span>
    </button>
  </div>

  <a class="nav-link" onclick={ goHub }>← BACK TO HUB</a>

  <script>
    import * as gameStore from '../../game-store.js'
    import { navigateState, sysNotify } from '../../app-states.js'
    import { playEffect } from '../../engine/audio.js'
    import { persistenceService } from '../../services/persistence-service.ts'
    import { LOBBY_SELECTION_KEY } from '../lobby/lobby-state.ts'

    const LOADING_ROWS = [0, 1, 2, 3]

    export default {
      state: {
        worlds: [],
        loading: true,
        error: null,
        selectingWorldId: null,
        selectedWorldSaveId: null,
      },

      onMounted() {
        this.restoreSelection()
        this.loadWorlds()
      },

      restoreSelection() {
        try {
          const raw = sessionStorage.getItem(LOBBY_SELECTION_KEY)
          if (!raw) return
          const parsed = JSON.parse(raw)
          this.update({ selectedWorldSaveId: String(parsed?.selectedWorldSaveId || '').trim() || null })
        } catch {}
      },

      persistSelection() {
        try {
          const raw = sessionStorage.getItem(LOBBY_SELECTION_KEY)
          const parsed = raw ? JSON.parse(raw) : {}
          sessionStorage.setItem(LOBBY_SELECTION_KEY, JSON.stringify({
            ...parsed,
            selectedWorldSaveId: this.state.selectedWorldSaveId || null,
          }))
        } catch {}
      },

      async loadWorlds() {
        this.update({ loading: true, error: null, worlds: [] })
        try {
          const worlds = await persistenceService.listWorlds()
          const selectedWorldSaveId = worlds.some(w => w.saveId === this.state.selectedWorldSaveId)
            ? this.state.selectedWorldSaveId
            : null
          this.update({ worlds, loading: false, selectedWorldSaveId })
          this.persistSelection()
        } catch {
          this.update({ loading: false, error: 'Error loading world gallery.' })
        }
      },

      async selectWorld(e) {
        const saveId = String(e?.currentTarget?.dataset?.saveId ?? '').trim()
        if (!saveId || this.state.selectingWorldId) return
        playEffect('ui_open')
        this.update({ selectingWorldId: saveId, error: null })
        try {
          const selected = await gameStore.importWorldFromSave(saveId)
          if (!selected) throw new Error('World save unavailable')
          this.update({ selectedWorldSaveId: saveId, selectingWorldId: null })
          this.persistSelection()
          sysNotify('World loaded. Choose your vessel.')
          navigateState('vessels')
        } catch (err) {
          console.error(err)
          this.update({ selectingWorldId: null, error: 'Failed to load this world.' })
        }
      },

      forgeFresh() {
        gameStore.setImportedWorldData(null)
        navigateState('forging')
      },

      goHub() {
        navigateState('hub')
      },

      worldSeedLabel(seed) {
        const firstLine = String(seed ?? '').split('\n')[0].trim()
        if (!firstLine) return 'UNTITLED WORLD'
        if (firstLine.length <= 66) return firstLine
        return `${firstLine.slice(0, 66)}...`
      },

      worldTimestamp(ts) {
        const parsed = Number(ts ?? 0)
        if (!Number.isFinite(parsed) || parsed <= 0) return 'Unknown date'
        return new Date(parsed).toLocaleString()
      },

      worldOriginLabel(origin) {
        const key = String(origin ?? '').toLowerCase()
        if (key === 'imported_blueprint') return 'IMPORTED WORLD'
        if (key === 'imported_starter') return 'IMPORTED STARTER'
        if (key === 'imported_scenario') return 'IMPORTED GAME'
        if (key === 'imported_vessel') return 'IMPORTED VESSEL'
        return 'ORIGINAL'
      }
    }
  </script>

  <style>
    worlds-view {
      display: flex;
      flex-direction: column;
      gap: var(--space-lg);
      width: 100%;
      max-width: min(100%, 78rem);
    }

    .lobby-section-title {
      color: var(--text-muted);
      font-size: 0.92rem;
      letter-spacing: 0.14em;
      border-bottom: 1px solid var(--border);
      padding-bottom: var(--space-sm);
      margin: 0;
    }

    .worlds-flow-hint {
      color: var(--text-prose);
      font-size: var(--space-md);
      letter-spacing: 0.03em;
      margin: -0.15rem 0 0;
      line-height: 1.55;
    }

    .worlds-forge-btn {
      margin-bottom: var(--space-xs);
    }

    .save-list {
      display: flex;
      flex-direction: column;
      gap: 0.625rem;
      max-height: 50vh;
      overflow-y: auto;
    }

    .world-slot {
      width: 100%;
      border: 1px solid var(--border);
      border-radius: var(--radius-lg);
      background:
        linear-gradient(135deg, color-mix(in srgb, var(--surface) 84%, var(--bg)), color-mix(in srgb, var(--surface-card-1) 84%, #03060b)),
        radial-gradient(circle at 16% 16%, color-mix(in srgb, var(--gold) 12%, transparent), transparent 12%);
      color: var(--text-muted);
      font-family: inherit;
      text-align: left;
      padding: clamp(var(--space-sm), 2vw, var(--space-md));
      display: grid;
      gap: var(--space-xs);
      cursor: pointer;
      position: relative;
      overflow: clip;
      transform-style: preserve-3d;
      font-size: var(--space-md);
      box-shadow:
        0 1.25rem 2.25rem rgba(0, 0, 0, 0.22),
        0 0 0 1px rgba(255, 255, 255, 0.03),
        inset 0 1px 0 rgba(255, 255, 255, 0.06);
      transition:
        border-color 0.18s ease,
        transform 0.24s ease,
        box-shadow 0.24s ease;
    }

    .world-slot.loading-skeleton {
      cursor: progress;
      pointer-events: none;
      color: var(--text-dim);
      border-color: color-mix(in srgb, var(--border) 86%, #61708a);
      background:
        linear-gradient(180deg, color-mix(in srgb, var(--surface-card-2) 94%, var(--bg)), color-mix(in srgb, var(--surface-card-3) 90%, #03060b)),
        radial-gradient(circle at 18% 18%, color-mix(in srgb, var(--gold) 6%, transparent), transparent 28%);
      box-shadow: var(--shadow-elev-2);
      animation: slot-stage-in 0.4s cubic-bezier(0.22, 0.9, 0.22, 1.12) both;
    }

    .world-slot:hover,
    .world-slot:focus-visible {
      border-color: var(--gold);
      transform: perspective(81.25rem) translateY(-0.36rem) rotateX(4deg) scale(1.012);
      box-shadow:
        0 1.625rem 2.625rem rgba(0, 0, 0, 0.28),
        0 0 0 1px color-mix(in srgb, var(--gold) 16%, transparent),
        inset 0 1px 0 rgba(255, 255, 255, 0.09);
    }

    .world-slot:disabled {
      cursor: not-allowed;
      opacity: 0.7;
    }

    .world-slot-topline {
      display: flex;
      justify-content: space-between;
      gap: var(--space-sm);
      align-items: flex-start;
    }

    .world-slot-head {
      display: flex;
      gap: var(--space-xs);
      align-items: baseline;
      min-width: 0;
      flex-wrap: wrap;
    }

    .world-slot-head b {
      color: var(--gold);
      font-size: 1em;
      letter-spacing: 0.05em;
    }

    .world-slot-head small {
      color: var(--text-muted);
      font-size: 0.72em;
      letter-spacing: 0.08em;
    }

    .world-slot-preview {
      display: grid;
      gap: 0.32rem;
      padding: 0.7em 0.78em;
      border-radius: 0.95em;
      border: 1px solid color-mix(in srgb, var(--gold) 16%, transparent);
      background:
        linear-gradient(180deg, color-mix(in srgb, var(--surface-card-2) 84%, #08101a), color-mix(in srgb, var(--surface-card-3) 12%, #03060b)),
        radial-gradient(circle at top right, color-mix(in srgb, var(--gold) 24%, transparent), transparent 48%);
      box-shadow:
        inset 0 1px 0 rgba(255, 255, 255, 0.04),
        0 0.875rem 1.5rem rgba(0, 0, 0, 0.14);
    }

    .world-slot-room {
      font-size: 0.98em;
      letter-spacing: 0.025em;
      line-height: 1.4;
      color: var(--text-prose);
      font-weight: 620;
    }

    .world-slot-timestamp {
      font-size: calc(0.7em + 0.01rem);
      letter-spacing: 0.08em;
      color: var(--text-muted);
      text-transform: uppercase;
    }

    .world-slot-meta {
      display: flex;
      flex-wrap: wrap;
      gap: var(--space-xs) var(--space-sm);
    }

    .world-slot-signal {
      display: inline-flex;
      align-items: center;
      min-height: 2.1em;
      padding: 0.35em 0.7em;
      border-radius: 62.4375rem;
      border: 1px solid color-mix(in srgb, var(--border) 84%, #41536c);
      background: color-mix(in srgb, var(--surface-card-3) 84%, var(--bg));
      font-size: calc(0.72em + 0.01rem);
      color: var(--text-muted);
      letter-spacing: 0.04em;
    }

    .world-slot-signal.vessel {
      border-color: color-mix(in srgb, var(--brand-primary) 16%, transparent);
      color: var(--brand-primary);
    }

    .world-slot-route {
      border: 1px solid color-mix(in srgb, var(--gold) 16%, transparent);
      background: color-mix(in srgb, var(--gold) 24%, rgba(8, 12, 18, 0.92));
      color: var(--gold);
      border-radius: 62.4375rem;
      padding: 0.18rem var(--space-xs);
      font-size: 0.68em;
      letter-spacing: 0.12em;
      white-space: nowrap;
      align-self: flex-start;
      opacity: 0.74;
    }

    .world-slot-pending {
      color: var(--gold);
      font-size: 0.74em;
      letter-spacing: 0.08em;
      font-weight: bold;
    }
  </style>
</worlds-view>
